import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Star } from 'lucide-react';
import Navbar from './components/shared/Navbar';
import Footer from './components/shared/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const WELLY_API = `${BACKEND_URL}/pu`;

// ── Paramètres des carousels ─────────────────────────────────────────────
// Modifier ici pour ajuster le comportement sans toucher au reste du code.
const CAROUSEL_CONFIG = {
  confiance: {
    intervalSec:  4,   // secondes entre chaque glissement automatique
    itemsPerSlide: 3,  // nb de logos glissés à chaque intervalle
  },
  commerces: {
    intervalSec:  5,   // secondes entre chaque glissement automatique
    itemsPerSlide: 4,  // nb de logos glissés à chaque intervalle
  },
  temoignages: {
    intervalSec:  7,   // secondes entre chaque témoignage automatique
  },
};

// ── Carousel témoignages avec avance automatique + swipe/drag ────────────
function TemoignagesCarousel({ testimonials }) {
  const [current, setCurrent] = useState(0);
  const [paused,  setPaused]  = useState(false);
  const total = testimonials.length;

  const prev = () => setCurrent(i => (i - 1 + total) % total);
  const next = () => setCurrent(i => (i + 1) % total);

  // Avance automatique
  useEffect(() => {
    if (total <= 1 || paused) return;
    const timer = setInterval(next, CAROUSEL_CONFIG.temoignages.intervalSec * 1000);
    return () => clearInterval(timer);
  }, [total, paused, current]);

  // ── Drag souris + swipe tactile ─────────────────────────────────────────
  const dragStartX = useRef(null);
  const THRESHOLD  = 60; // px minimum pour déclencher un changement

  const onDragStart = (clientX) => { dragStartX.current = clientX; };
  const onDragEnd   = (clientX) => {
    if (dragStartX.current === null) return;
    const delta = clientX - dragStartX.current;
    if (Math.abs(delta) > THRESHOLD) {
      delta < 0 ? next() : prev();
    }
    dragStartX.current = null;
  };

  const satisfaction = total
    ? Math.round(testimonials.filter(t => (t.note || 5) >= 4).length / total * 100) : 96;
  const t = testimonials[current] || {};
  const initials = t.nom ? t.nom.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';

  return (
    <div className="temoignages-layout"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="temoignages-left">
        <div className="temoignages-badge">
          <div className="temoignages-avatars">
            {['👩🏾','👨🏿','👩🏽','👨🏾'].map((e,i) => (
              <span key={i} className="temoignages-avatar">{e}</span>
            ))}
          </div>
          <span className="text-sm font-medium text-gray-700">{satisfaction}% Satisfaction</span>
          <span className="text-sm text-gray-500 ml-2">⭐ Avis très positif</span>
        </div>
        <h2 className="temoignages-title">
          Ils parlent de <span style={{color:'var(--primary)'}}>Welly</span>
        </h2>
        <p className="temoignages-subtitle">
          Commerçants, entreprises et particuliers partagent ce que Welly leur apporte au quotidien
        </p>
      </div>

      {total > 1 && (
        <div className="temoignages-nav">
          <button className="temoignage-nav-btn prev" onClick={prev}>←</button>
          <button className="temoignage-nav-btn next" onClick={next}>→</button>
        </div>
      )}

      {/* Carte — swipeable */}
      <div className="temoignages-right">
        <div
          className="temoignage-card-large"
          style={{cursor: total > 1 ? 'grab' : 'default'}}
          onMouseDown={e => onDragStart(e.clientX)}
          onMouseUp={e => onDragEnd(e.clientX)}
          onMouseLeave={() => { dragStartX.current = null; }}
          onTouchStart={e => onDragStart(e.touches[0].clientX)}
          onTouchEnd={e => onDragEnd(e.changedTouches[0].clientX)}
        >
          <div className="temoignage-quote-icon">❝</div>
          <p className="temoignage-texte">{t.texte}</p>
          <div className="temoignage-author-row">
            <div className="temoignage-avatar-circle">{initials}</div>
            <div className="temoignage-author-info">
              <p className="temoignage-author-name">{t.nom}</p>
              <p className="temoignage-author-role">{t.role}</p>
            </div>
            <div className="temoignage-stars">
              {[...Array(Math.round(t.note || 5))].map((_, i) => (
                <Star key={i} size={16} fill="var(--secondary)" color="var(--secondary)" />
              ))}
              <span className="temoignage-note">{(t.note || 5).toFixed(1)}</span>
            </div>
          </div>
        </div>

        {total > 1 && (
          <div className="temoignage-dots">
            {testimonials.map((_, i) => (
              <span key={i} className={`temoignage-dot${i === current ? ' active' : ''}`}
                onClick={() => setCurrent(i)} style={{cursor:'pointer'}} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Carousel logos "Ils nous ont fait confiance" ─────────────────────────
const CONFIANCE_LOGOS = [
  { nom: 'Air Sénégal', logo: '/assets/images/logo-airsenegal.png' },
  { nom: 'Expresso',    logo: '/assets/images/logo-expresso.png' },
  { nom: 'Orange',      logo: '/assets/images/logo-orange.png' },
  { nom: 'Paps',        logo: '/assets/images/logo-paps.png' },
  { nom: 'CoinAfrique', logo: '/assets/images/logo-coinafrique.png' },
  { nom: 'Vivo Energy', logo: '/assets/images/logo-vivoenergy.png' },
  { nom: 'FGI',         logo: '/assets/images/logo-fgi.png' },
  { nom: 'DAnalytica',  logo: '/assets/images/logo-danalytica.png' },
  { nom: 'Sablux',      logo: '/assets/images/logo-sablux.png' },
];

// ── Carousel logos avec glissement par intervalle + drag/swipe ───────────
// config       : objet { intervalSec, itemsPerSlide } issu de CAROUSEL_CONFIG
// previewMax   : nb d'éléments max dans le carousel (le reste est dans la modale)
function LogoCarousel({ items, onVoirPlus, btnLabel, cardClass = 'confiance-card-lg', renderCard, config, previewMax }) {
  const maskRef      = useRef(null);
  const intervalRef  = useRef(null);
  const paused       = useRef(false);
  const dragging     = useRef(false);
  const dragStartX   = useRef(0);
  const dragStartScroll = useRef(0);

  const displayed = previewMax ? items.slice(0, previewMax) : items;
  const { intervalSec = 4, itemsPerSlide = 3 } = config || {};

  // Glisser vers la droite de N cartes avec scroll fluide + boucle infinie
  const slideNext = () => {
    const el = maskRef.current;
    if (!el) return;
    // Utiliser la première classe seulement (cardClass peut contenir plusieurs classes)
    const firstClass = cardClass.split(' ')[0];
    const card = el.querySelector(`.${firstClass}`);
    if (!card) return;
    const step = (card.offsetWidth + 20) * itemsPerSlide;
    el.scrollBy({ left: step, behavior: 'smooth' });
    setTimeout(() => {
      const oneThird = el.scrollWidth / 3;
      if (el.scrollLeft >= oneThird * 2) el.scrollLeft -= oneThird;
    }, 450);
  };

  // Lancer l'intervalle automatique
  const startInterval = () => {
    stopInterval();
    intervalRef.current = setInterval(() => {
      if (!paused.current && !dragging.current) slideNext();
    }, intervalSec * 1000);
  };
  const stopInterval = () => clearInterval(intervalRef.current);

  useEffect(() => {
    const el = maskRef.current;
    if (!el) return;
    // Positionner au milieu du triple pour pouvoir glisser dans les deux sens
    requestAnimationFrame(() => {
      el.scrollLeft = el.scrollWidth / 3;
    });
    startInterval();
    return stopInterval;
  }, [displayed.length]);

  // Boucle infinie côté gauche (drag vers la gauche)
  const checkLoop = () => {
    const el = maskRef.current;
    if (!el) return;
    const oneThird = el.scrollWidth / 3;
    if (el.scrollLeft <= 0)            el.scrollLeft += oneThird;
    if (el.scrollLeft >= oneThird * 2) el.scrollLeft -= oneThird;
  };

  // ── Souris ───────────────────────────────────────────────────────────────
  const onMouseDown = (e) => {
    dragging.current     = true;
    dragStartX.current   = e.pageX;
    dragStartScroll.current = maskRef.current.scrollLeft;
    maskRef.current.style.cursor = 'grabbing';
    e.preventDefault();
  };
  const onMouseMove = (e) => {
    if (!dragging.current) return;
    maskRef.current.scrollLeft = dragStartScroll.current - (e.pageX - dragStartX.current);
    checkLoop();
  };
  const onMouseUp = () => {
    if (!dragging.current) return;
    dragging.current = false;
    if (maskRef.current) maskRef.current.style.cursor = 'grab';
    startInterval(); // réinitialise le timer après un drag
  };

  // ── Tactile ──────────────────────────────────────────────────────────────
  const onTouchStart = (e) => {
    dragging.current       = true;
    dragStartX.current     = e.touches[0].pageX;
    dragStartScroll.current = maskRef.current.scrollLeft;
    stopInterval();
  };
  const onTouchMove = (e) => {
    if (!dragging.current) return;
    maskRef.current.scrollLeft = dragStartScroll.current - (e.touches[0].pageX - dragStartX.current);
    checkLoop();
  };
  const onTouchEnd = () => {
    dragging.current = false;
    startInterval();
  };

  const slidePrev = () => {
    const el = maskRef.current;
    if (!el) return;
    const firstClass = cardClass.split(' ')[0];
    const card = el.querySelector(`.${firstClass}`);
    if (!card) return;
    const step = (card.offsetWidth + 20) * itemsPerSlide;
    el.scrollBy({ left: -step, behavior: 'smooth' });
    setTimeout(() => {
      const oneThird = el.scrollWidth / 3;
      if (el.scrollLeft <= 0) el.scrollLeft += oneThird;
    }, 450);
  };

  return (
    <div className="logo-carousel-outer">
      {/* Rangée : flèche gauche + piste + flèche droite */}
      <div className="logo-carousel-row">
        <button className="carousel-arrow carousel-arrow-left" onClick={() => { slidePrev(); startInterval(); }}>‹</button>
        <div
          ref={maskRef}
          className="logo-carousel-mask-js"
          style={{cursor:'grab'}}
          onMouseEnter={() => { paused.current = true;  stopInterval(); }}
          onMouseLeave={() => { paused.current = false; onMouseUp(); startInterval(); }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <div className="logo-carousel-track-js">
            {[...displayed, ...displayed, ...displayed].map((e, i) => (
              <div key={i} className={cardClass} style={{userSelect:'none', pointerEvents:'none'}}>
                {renderCard(e)}
              </div>
            ))}
          </div>
        </div>
        <button className="carousel-arrow carousel-arrow-right" onClick={() => { slideNext(); startInterval(); }}>›</button>
      </div>
      <div className="logo-carousel-footer">
        <button className="confiance-voir-plus" onClick={onVoirPlus}>
          {btnLabel} <span className="btn-welly-badge" style={{display:'inline-flex'}}>↗</span>
        </button>
      </div>
    </div>
  );
}

// ── Modal "Voir tout" avec recherche ────────────────────────────────────
function VoirToutModal({ title, items, renderItem, onClose, searchPlaceholder = 'Rechercher...' }) {
  const [query, setQuery] = useState('');
  const filtered = query.trim()
    ? items.filter(item => {
        const name = (item.nom || item.shopName || '').toLowerCase();
        return name.includes(query.toLowerCase());
      })
    : items;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">{title} <span className="modal-count">({filtered.length})</span></h3>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {items.length >= 8 && (
          <div className="modal-search-bar">
            <span className="modal-search-icon">🔍</span>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="modal-search-input"
              autoFocus
            />
            {query && (
              <button className="modal-search-clear" onClick={() => setQuery('')}>✕</button>
            )}
          </div>
        )}
        <div className="modal-grid">
          {filtered.length > 0
            ? filtered.map((item, i) => renderItem(item, i))
            : <p style={{gridColumn:'1/-1', textAlign:'center', color:'#999', padding:'40px 0'}}>Aucun résultat pour « {query} »</p>
          }
        </div>
      </div>
    </div>
  );
}

// ── Carousel swipeable pour mobile (sections institutions/commercants/entreprises) ──
// ── Carousel swipeable mobile — loop infini naturel ──────────────────────
// Reçoit les cartes en tant qu'items[] pour pouvoir dupliquer premier/dernier
function MobileSwipeInner({ items, className = '' }) {
  const trackRef  = useRef(null);
  const startX    = useRef(null);
  const isMoving  = useRef(false);
  const n = items.length;

  // Disposition : [clone_dernier, ...items, clone_premier]
  const allSlides = [items[n - 1], ...items, items[0]];

  // Positionner sur la vraie première carte au montage
  useEffect(() => {
    const el = trackRef.current;
    if (el) el.scrollLeft = el.offsetWidth;
  }, [n]);

  const curIdx = () => {
    const el = trackRef.current;
    if (!el) return 1;
    return Math.round(el.scrollLeft / el.offsetWidth);
  };

  const goTo = (idx, behavior = 'smooth') => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * el.offsetWidth, behavior });
  };

  // Téléportation silencieuse quand on atteint un clone
  const handleScroll = () => {
    const el = trackRef.current;
    if (!el) return;
    const idx = curIdx();
    if (idx === 0) {
      // Clone de la dernière → téléporter sur la vraie dernière
      requestAnimationFrame(() => { el.scrollLeft = n * el.offsetWidth; });
    } else if (idx === n + 1) {
      // Clone de la première → téléporter sur la vraie première
      requestAnimationFrame(() => { el.scrollLeft = el.offsetWidth; });
    }
  };

  const prev = () => goTo(curIdx() - 1);
  const next = () => goTo(curIdx() + 1);

  const onTouchStart = e => {
    startX.current = e.touches[0].clientX;
    isMoving.current = false;
  };
  const onTouchMove = () => { isMoving.current = true; };
  const onTouchEnd = e => {
    if (!isMoving.current || startX.current === null) return;
    const delta = startX.current - e.changedTouches[0].clientX;
    if (Math.abs(delta) > 40) { delta > 0 ? next() : prev(); }
    startX.current = null;
    isMoving.current = false;
  };

  return (
    <div className={`mobile-swipe-outer ${className}`}>
      <button className="mobile-swipe-btn" onClick={prev}>‹</button>
      <div
        ref={trackRef}
        className="mobile-swipe-track"
        onScroll={handleScroll}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {allSlides.map((item, i) => (
          <div key={i} className="mobile-swipe-card">{item}</div>
        ))}
      </div>
      <button className="mobile-swipe-btn" onClick={next}>›</button>
    </div>
  );
}

// Wrapper public — gère le cas desktop (grille normale) et mobile (carousel)
function MobileSwipe({ children, className = '' }) {
  // Sur desktop, afficher normalement
  // Sur mobile, MobileSwipeInner reçoit les enfants du div grille
  const gridChild = React.Children.toArray(children)[0];
  const cards = gridChild ? React.Children.toArray(gridChild.props.children) : [];

  if (cards.length <= 1) return <div className={className}>{children}</div>;

  return (
    <>
      {/* Version desktop — grille normale */}
      <div className="mobile-swipe-desktop">{children}</div>
      {/* Version mobile/tablette — carousel */}
      <MobileSwipeInner items={cards} className={className} />
    </>
  );
}

function App() {
  const navigate = useNavigate();

  const saveAndNavigate = (path, state) => {
    sessionStorage.setItem('homeScrollPosition', window.scrollY.toString());
    navigate(path, state ? { state } : undefined);
  };

  const [commerces, setCommerces] = useState([]);
  const [testimonials, setTestimonials] = useState([
    { id: 1, nom: 'Maimouna Fall',   role: 'Commerçante partenaire',           texte: "Depuis que nous avons rejoint Welly, nos clients reviennent beaucoup plus souvent. Le programme est simple à utiliser et on voit clairement l'impact sur notre chiffre d'affaires.", note: 5 },
    { id: 2, nom: 'Ibrahima Diallo', role: 'Directeur RH, PME sénégalaise',    texte: "Welly nous a permis de récompenser nos équipes de façon concrète et rapide. Les salariés adorent pouvoir utiliser leurs points dans des commerces qu'ils fréquentent déjà.", note: 5 },
    { id: 3, nom: 'Fatou Sow',       role: 'Gérante de boutique, Dakar',       texte: "J'ai vu une vraie différence depuis que j'ai rejoint Welly. Mes clients fidèles reviennent plus souvent et certains m'ont dit qu'ils restaient chez moi pour les points.", note: 5 },
    { id: 4, nom: 'Moussa Ndiaye',   role: 'Responsable Marketing',            texte: "La solution est simple à déployer et les salariés l'ont adoptée très rapidement. C'est exactement ce qu'il nous fallait pour moderniser notre politique de récompenses.", note: 4 },
    { id: 5, nom: 'Aminata Traoré',  role: 'Commerçante partenaire, Plateau',  texte: "Ce que j'apprécie avec Welly c'est que c'est simple. Pas besoin de former mon personnel, tout se fait dans l'appli. Et le support répond vite quand on a une question.", note: 5 },
    { id: 6, nom: 'Cheikh Bâ',       role: "Chef d'entreprise, Logistique",    texte: "Nous utilisons Welly pour les paniers de fin d'année et les cartes cadeaux. C'est bien plus pratique qu'avant et ça fait vraiment plaisir aux équipes.", note: 4 },
  ]);
  const [commercesLoading, setCommercesLoading] = useState(true);
  const [showConfianceModal, setShowConfianceModal] = useState(false);
  const [showCommercesModal, setShowCommercesModal] = useState(false);

  const stats = React.useMemo(() => {
    if (!testimonials.length) return { note_moyenne: 4.9, nombre_avis: 262 };
    const avg = testimonials.reduce((s, t) => s + (t.note || 5), 0) / testimonials.length;
    return { note_moyenne: Math.round(avg * 10) / 10, nombre_avis: testimonials.length };
  }, [testimonials]);

  useEffect(() => { fetchCommerces(); }, []);

  const fetchCommerces = async () => {
    try {
      setCommercesLoading(true);
      const res = await axios.get(`${WELLY_API}/seller/all/v2?page=0&size=200`);
      const mapped = (res.data.content || []).map(item => ({
        id: item.id,
        nom: item.shopName,
        logo_url: `${WELLY_API}/pictures/seller-logo/${item.id}`,
        reduction_percentage: item.voucherOfferPercent || item.walletOfferPercent || null,
        category: item.categoryName || null,
      }));
      setCommerces(mapped);
    } catch (e) { console.error('Erreur commerces:', e); }
    finally { setCommercesLoading(false); }
  };

  // Carousel commerces : géré par LogoCarousel (défilement CSS continu)

  return (
    <div className="App">
      <Navbar />

      {/* Hero */}
      <section className="hero-section">
        <div className="container-1440">
          <div className="hero-inner">
            <div className="hero-left">
              <div className="flex items-center gap-2 mb-6">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => <span key={i} className="hero-star">★</span>)}
                </div>
                <span className="font-semibold text-gray-700 text-sm">{stats.note_moyenne} ({stats.nombre_avis})</span>
              </div>
              <h1 className="hero-title">
                Fidélisation.<br />Récompenses.<br />
                <span style={{color:'var(--primary)'}}>Bons plans.</span>
              </h1>
              <div className="hero-title-underline" />
              <p className="hero-subtitle mb-8">
                Welly aide les commerçants à fidéliser leurs clients, les entreprises à récompenser leurs équipes et les particuliers à mieux consommer au quotidien
              </p>
              <div className="flex gap-6 mb-8">
                <a href="#commercants" className="hero-category-link">
                  <img src="/assets/icons/icon-commercants.png" alt="" className="w-5 h-5 object-contain" />
                  <span>Commerçants</span>
                </a>
                <a href="#entreprises" className="hero-category-link">
                  <img src="/assets/icons/icon-entreprises.png" alt="" className="w-5 h-5 object-contain" />
                  <span>Entreprises</span>
                </a>
              </div>
              <div className="flex flex-wrap gap-4 mb-6">
                <button onClick={() => saveAndNavigate('/devenir-partenaire')} className="btn-welly-outline">
                  Devenir partenaire <span className="btn-welly-badge">↗</span>
                </button>
                <button onClick={() => saveAndNavigate('/demander-devis')} className="btn-welly-outline">
                  Demander un devis <span className="btn-welly-badge">↗</span>
                </button>
              </div>
            </div>
            <div className="hero-images-wrapper">
              <div className="hero-combined-wrapper">
                <img src="/assets/images/hero-combined.png" alt="Welly App" className="hero-combined-img"
                  onClick={() => document.getElementById('particuliers')?.scrollIntoView({behavior:'smooth'})}
                  style={{cursor:'pointer'}} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pour les institutions */}
      <section className="py-20 bg-white" id="institutions">
        <div className="container-1440 px-8">
          <h2 className="section-title w-full">Pour les <span style={{color:'var(--secondary)'}}>institutions</span> et grandes marques</h2>
          <p className="section-subtitle">Conçu pour les banques, assurances, compagnies aériennes, stations service et grandes organisations souhaitant enrichir et dynamiser leurs programmes de fidélité</p>
          <MobileSwipe className="md:block"><div className="grid md:grid-cols-3 gap-8 w-full mobile-swipe-grid">
            <div className="card-yellow mobile-swipe-card">
              <div className="card-illustration-area"><img src="/assets/images/illus-institution1.png" alt="Conversion de points" className="card-illustration" /></div>
              <h3 className="card-title">Conversion de points</h3>
              <p className="card-description">Une solution simple pour moderniser vos programmes de fidélité et renforcer l'engagement de vos clients</p>
              <ul className="feature-list">
                {['Convertissez vos points en chèques cadeaux Welly','Récompenses utilisables dans tout le réseau','Idéal pour compagnies et clients premium','Distribution simple et rapide'].map(t => (
                  <li key={t} className="feature-item"><span className="feature-arrow-circle"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M6 3l2 2-2 2" stroke="#201D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="card-pink mobile-swipe-card">
              <div className="card-illustration-area"><img src="/assets/icons/illus-institution2.svg" alt="Réseau d'avantages" className="card-illustration" /></div>
              <h3 className="card-title">Réseau d'avantages</h3>
              <p className="card-description">Créez plus de valeur pour vos clients grâce à un programme d'avantages moderne, attractif et qui enrichit l'expérience client au quotidien</p>
              <ul className="feature-list">
                {['Accès au réseau de partenaires Welly','Réductions dans de nombreux commerces','Avantages activables immédiatement','Expérience enrichie pour vos clients'].map(t => (
                  <li key={t} className="feature-item"><span className="feature-arrow-circle"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M6 3l2 2-2 2" stroke="#201D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="card-orange mobile-swipe-card">
              <div className="card-illustration-area"><img src="/assets/icons/illus-institution3.svg" alt="Dynamisez" className="card-illustration" /></div>
              <h3 className="card-title">Dynamisez votre programme</h3>
              <p className="card-description">Transformez votre programme de fidélité en un véritable levier d'engagement grâce à des expériences attractives</p>
              <ul className="feature-list">
                {['Enrichissez votre programme de fidélité','Offrez des avantages concrets au quotidien','Renforcez l\'engagement de vos clients','Lancez facilement des campagnes de récompense'].map(t => (
                  <li key={t} className="feature-item"><span className="feature-arrow-circle"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M6 3l2 2-2 2" stroke="#201D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span><span>{t}</span></li>
                ))}
              </ul>
            </div>
          </div></MobileSwipe>
          <div className="section-btn-center">
            <button onClick={() => saveAndNavigate('/demander-devis', {source:'institution'})} className="btn-welly-outline-lg">
              Demander une présentation <span className="btn-welly-badge">↗</span>
            </button>
          </div>
        </div>
      </section>
      <section className="py-20 bg-gray-50" id="commercants">
        <div className="container-1440 px-8">
          <h2 className="section-title w-full">Pour les <span style={{color:'var(--secondary)'}}>commerçants</span></h2>
          <p className="section-subtitle">Faites revenir vos clients et gagnez en visibilité sans complexité</p>
          <MobileSwipe className="md:block"><div className="grid md:grid-cols-3 gap-8 w-full mobile-swipe-grid">
            <div className="card-yellow mobile-swipe-card">
              <div className="card-illustration-area"><img src="/assets/images/illus-commerce1.png" alt="Fidélité" className="card-illustration" /></div>
              <h3 className="card-title">Créer un programme de fidélité</h3>
              <p className="card-description">Faites revenir vos clients plus souvent, sans complexité.</p>
              <ul className="feature-list">
                {['Définissez votre budget de fidélité','Choisissez vos règles de récompenses','Les gains sont automatiques pour vos clients','Suivez l\'impact sur la fréquence d\'achat'].map(t => (
                  <li key={t} className="feature-item"><span className="feature-arrow-circle"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M6 3l2 2-2 2" stroke="#201D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="card-pink mobile-swipe-card">
              <div className="card-illustration-area"><img src="/assets/icons/illus-institution2.svg" alt="Visibilité" className="card-illustration" /></div>
              <h3 className="card-title">Avoir une visibilité</h3>
              <p className="card-description">Attirez de nouveaux clients grâce aux entreprises partenaires Welly.</p>
              <ul className="feature-list">
                {['Être référencé auprès des entreprises clientes','Accepter les cartes cadeaux et bons d\'achat Welly','Transformer les bénéficiaires en clients réguliers','Augmenter le panier moyen et la récurrence'].map(t => (
                  <li key={t} className="feature-item"><span className="feature-arrow-circle"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M6 3l2 2-2 2" stroke="#201D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span><span>{t}</span></li>
                ))}
              </ul>
            </div>
            <div className="card-orange mobile-swipe-card">
              <div className="card-illustration-area"><img src="/assets/images/illus-commerce3.png" alt="Attirer" className="card-illustration" /></div>
              <h3 className="card-title">Attirer de nouveaux clients</h3>
              <p className="card-description">Gagnez en visibilité et attirez des clients déjà habitués aux bons plans.</p>
              <ul className="feature-list">
                {['Apparaître dans l\'application Welly','Proposer des offres simples et visibles','Attirer de nouveaux clients autour de vous','Booster votre visibilité locale au bon moment'].map(t => (
                  <li key={t} className="feature-item"><span className="feature-arrow-circle"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M6 3l2 2-2 2" stroke="#201D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span><span>{t}</span></li>
                ))}
              </ul>
            </div>
          </div></MobileSwipe>
          <div className="section-btn-center">
            <button onClick={() => saveAndNavigate('/devenir-partenaire')} className="btn-welly-outline-lg">
              Devenir partenaire <span className="btn-welly-badge">↗</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pour les entreprises */}
      <section className="py-20 bg-white" id="entreprises">
        <div className="container-1440 px-8">
          <h2 className="section-title w-full">Pour les <span style={{color:'var(--secondary)'}}>entreprises</span></h2>
          <p className="section-subtitle">Soutenez le pouvoir d'achat de vos collaborateurs avec des avantages concrets</p>
          <MobileSwipe className="md:block"><div className="grid md:grid-cols-3 gap-8 w-full mobile-swipe-grid">
            <div className="card-yellow mobile-swipe-card">
              <div className="card-illustration-area"><img src="/assets/images/illus-institution1.png" alt="Récompenser" className="card-illustration" /></div>
              <h3 className="card-title">Récompenser vos équipes</h3>
              <p className="card-description">Récompensez vos équipes avec des avantages utiles et concrets.</p>
              <ul className="feature-list">
                {['Cadeaux salariés (Tabaski, Ramadan, etc.)','Cartes cadeaux physiques ou digitales','Utilisables dans un large réseau de commerces','Gestion simple et centralisée'].map(t => (
                  <li key={t} className="feature-item"><span className="feature-arrow-circle"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M6 3l2 2-2 2" stroke="#201D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span><span>{t}</span></li>
                ))}
              </ul>
              <button onClick={() => saveAndNavigate('/demander-devis')} className="card-link mt-4">Commander des cartes ou chèques cadeaux →</button>
            </div>
            <div className="card-pink mobile-swipe-card">
              <div className="card-illustration-area"><img src="/assets/icons/illus-institution2.svg" alt="Pouvoir d'achat" className="card-illustration" /></div>
              <h3 className="card-title">Soutenir le pouvoir d'achat</h3>
              <p className="card-description">Soutenez le pouvoir d'achat de vos collaborateurs avec des paniers essentiels.</p>
              <ul className="feature-list">
                {['Riz, huile, sucre, produits essentiels','Paniers prêts à offrir','Acceptés dans le réseau Welly','Forte valeur sociale et humaine'].map(t => (
                  <li key={t} className="feature-item"><span className="feature-arrow-circle"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M6 3l2 2-2 2" stroke="#201D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span><span>{t}</span></li>
                ))}
              </ul>
              <button onClick={() => saveAndNavigate('/demander-devis')} className="card-link mt-4">Recevoir une proposition →</button>
            </div>
            <div className="card-orange mobile-swipe-card">
              <div className="card-illustration-area"><img src="/assets/icons/illus-institution3.svg" alt="Sur mesure" className="card-illustration" /></div>
              <h3 className="card-title">Solution sur mesure</h3>
              <p className="card-description">Des avantages adaptés à votre budget et à vos équipes.</p>
              <ul className="feature-list">
                {['Avantages personnalisés','Gestion centralisée','Suivi de l\'utilisation des avantages','Accompagnement dédié'].map(t => (
                  <li key={t} className="feature-item"><span className="feature-arrow-circle"><svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 5h6M6 3l2 2-2 2" stroke="#201D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg></span><span>{t}</span></li>
                ))}
              </ul>
              <button onClick={() => saveAndNavigate('/demander-devis')} className="card-link mt-4">Parler à un conseiller Welly →</button>
            </div>
          </div></MobileSwipe>
          <div className="section-btn-center">
            <button onClick={() => saveAndNavigate('/demander-devis')} className="btn-welly-outline-lg">
              Demander un devis <span className="btn-welly-badge">↗</span>
            </button>
          </div>
        </div>
      </section>

      {/* Particuliers */}
      <section className="particuliers-section py-20" id="particuliers">
        <div className="container-1440 px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-8" style={{color:'var(--primary)'}}>
                Nos solutions pour<br /><span style={{color:'var(--secondary)'}}>les particuliers</span>
              </h2>
              <div className="space-y-7">
                {[
                  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><rect x="2" y="7" width="20" height="14" rx="2" stroke="white" strokeWidth="1.8"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="white" strokeWidth="1.8"/><line x1="12" y1="12" x2="12" y2="16" stroke="white" strokeWidth="1.8" strokeLinecap="round"/><line x1="10" y1="14" x2="14" y2="14" stroke="white" strokeWidth="1.8" strokeLinecap="round"/></svg>, titre: 'Gagner du cashback', desc: 'Récupérez une partie de votre argent à chaque achat chez les commerces partenaires' },
                  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><ellipse cx="12" cy="8" rx="8" ry="3" stroke="white" strokeWidth="1.8"/><path d="M4 8v4c0 1.657 3.582 3 8 3s8-1.343 8-3V8" stroke="white" strokeWidth="1.8"/><path d="M4 12v4c0 1.657 3.582 3 8 3s8-1.343 8-3v-4" stroke="white" strokeWidth="1.8"/></svg>, titre: 'Cumuler des points de fidélité', desc: 'Chaque passage compte : plus vous achetez, plus vous gagnez de récompenses' },
                  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M20 12v10H4V12" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M22 7H2v5h20V7z" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 22V7" stroke="white" strokeWidth="1.8" strokeLinecap="round"/></svg>, titre: "Utiliser des bons d'achats", desc: "Transformez vos points en bons d'achat et utilisez-les simplement quand vous le souhaitez" },
                ].map(({ icon, titre, desc }) => (
                  <div key={titre} className="flex items-start gap-4">
                    <div className="particulier-icon-wrap flex-shrink-0">{icon}</div>
                    <div>
                      <h3 className="text-base font-bold mb-1" style={{color:'var(--primary)'}}>{titre}</h3>
                      <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <p className="font-semibold mb-4" style={{color:'var(--primary)'}}>Télécharger l'application</p>
                <div className="flex gap-3">
                  <a href="https://play.google.com/store/apps/details?id=com.wellyworkentreprise.wellywork.app.customer&hl=ln" target="_blank" rel="noopener noreferrer">
                    <img src="/assets/images/badge-playstore.png" alt="Google Play" className="h-12 object-contain" />
                  </a>
                  <a href="https://apps.apple.com/us/app/welly/id6738099419" target="_blank" rel="noopener noreferrer">
                    <img src="/assets/images/badge-appstore.png" alt="App Store" className="h-12 object-contain" />
                  </a>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <img src="/assets/images/app-mockup.png" alt="App Welly" className="particuliers-mockup" />
            </div>
          </div>
        </div>
      </section>

      {/* Ils nous ont fait confiance */}
      <section className="py-20 bg-white" id="confiance">
        <div className="container-1440 px-8">
          <h2 className="section-title w-full">Ils nous ont fait <span style={{color:'var(--secondary)'}}>confiance</span></h2>
          <p className="section-subtitle">Des entreprises qui font déjà appel à Welly pour récompenser et motiver leurs salariés</p>
          <LogoCarousel
            items={CONFIANCE_LOGOS}
            onVoirPlus={() => setShowConfianceModal(true)}
            btnLabel="Voir tous nos partenaires"
            config={CAROUSEL_CONFIG.confiance}
            renderCard={(e) => (<>
              <img src={e.logo} alt={e.nom} className="confiance-logo-lg" />
              <p className="confiance-nom">{e.nom}</p>
            </>)}
          />
        </div>
      </section>

      {/* Modal Confiance */}
      {showConfianceModal && (
        <VoirToutModal
          title="Tous nos partenaires entreprises"
          items={CONFIANCE_LOGOS}
          onClose={() => setShowConfianceModal(false)}
          renderItem={(e, i) => (
            <div key={i} className="modal-logo-card">
              <img src={e.logo} alt={e.nom} className="modal-logo-img" />
              <p className="modal-logo-nom">{e.nom}</p>
            </div>
          )}
        />
      )}

      {/* Les commerces partenaires */}
      <section className="py-20 bg-gray-50" id="partenaires">
        <div className="container-1440 px-8">
          <h2 className="section-title w-full" style={{textAlign:'center'}}>
            Les commerces <span style={{color:'var(--secondary)'}}>partenaires</span> de Welly
          </h2>
          <p className="section-subtitle">Découvrez les commerces où vous pouvez utiliser vos avantages Welly</p>
        </div>

        {commercesLoading ? (
          <div style={{display:'flex', gap:20, padding:'0 80px', overflow:'hidden'}}>
            {[...Array(4)].map((_, i) => (
              <div key={i} className="confiance-card-lg commerce-card-skeleton" />
            ))}
          </div>
        ) : commerces.length > 0 ? (
          <LogoCarousel
            items={commerces}
            onVoirPlus={() => { sessionStorage.setItem('homeScrollPosition', window.scrollY.toString()); navigate('/commerces'); }}
            btnLabel={commerces.length >= 300 ? `Voir tous les commerces (${commerces.length})` : 'Voir tous les commerces'}
            cardClass="confiance-card-lg commerce-card-hover"
            config={CAROUSEL_CONFIG.commerces}
            previewMax={24}
            renderCard={(commerce) => (<>
              {commerce.reduction_percentage && (
                <div className="commerce-badge-sm">{commerce.reduction_percentage}%</div>
              )}
              <img src={commerce.logo_url} alt={commerce.nom} className="confiance-logo-lg"
                style={{maxHeight:64}}
                onError={e => { e.target.style.display='none'; e.target.nextSibling && (e.target.nextSibling.style.display='flex'); }} />
              <div className="commerce-logo-fallback" style={{display:'none', width:48, height:48, fontSize:22}}>
                {commerce.nom?.charAt(0)?.toUpperCase()}
              </div>
              <p className="confiance-nom">{commerce.nom}</p>
            </>)}
          />
        ) : (
          <p className="text-center text-gray-400 py-12">Aucun commerce partenaire disponible.</p>
        )}
      </section>

      {/* Ils parlent de Welly */}
      <section className="py-20 bg-white" id="témoignages">
        <div className="container-1440 px-8">
          <TemoignagesCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* Les avantages */}
      <section className="py-20 bg-gray-50">
        <div className="container-1440 px-8">
          <h2 className="section-title w-full">Les avantages de <span style={{color:'var(--secondary)'}}>Welly</span></h2>
          <p className="section-subtitle">Des avantages concrets, pensés pour le quotidien des commerçants, des entreprises et des clients</p>
          <MobileSwipe className="md:block"><div className="grid md:grid-cols-3 gap-8 w-full mobile-swipe-grid">
            {[
              { svg: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>, titre: 'Un écosystème unifié', desc: 'Tous vos programmes de fidélité, cashback et récompenses au même endroit' },
              { svg: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>, titre: 'Une expérience fluide', desc: 'Une seule application pour les clients, les commerçants et les entreprises' },
              { svg: <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--primary)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="15" x2="15" y2="15"/></svg>, titre: 'Sans engagement', desc: "Pas d'abonnement obligatoire, pas de matériel coûteux, activation rapide" },
            ].map(({ svg, titre, desc }) => (
              <div key={titre} className="advantage-item mobile-swipe-card">
                <div className="advantage-icon-wrap">{svg}</div>
                <h3 className="advantage-title">{titre}</h3>
                <p className="advantage-description">{desc}</p>
              </div>
            ))}
          </div></MobileSwipe>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <img src="/assets/images/cta-photo-left.png" alt="" className="cta-photo-left" />
        <img src="/assets/images/cta-photo-right.png" alt="" className="cta-photo-right" />
        <div className="cta-content">
          <h2 className="cta-title">Prêt a rejoindre l'écosystème <span style={{color:'var(--primary)'}}>Welly ?</span></h2>
          <p className="cta-subtitle">Une solution simple et sans contrainte pour récompenser autrement</p>
          <div className="cta-categories">
            {[{icon:'icon-commercants.png',label:'Commerçants'},{icon:'icon-entreprises.png',label:'Entreprises'},{icon:'icon-particuliers.png',label:'Particuliers'}].map(({icon,label}) => (
              <div key={label} className="cta-category">
                <img src={`/assets/icons/${icon}`} alt="" className="w-5 h-5 object-contain" /><span>{label}</span>
              </div>
            ))}
          </div>
          <div className="cta-buttons">
            <button onClick={() => saveAndNavigate('/devenir-partenaire')} className="btn-welly-outline">Devenir partenaire <span className="btn-welly-badge">↗</span></button>
            <button onClick={() => saveAndNavigate('/demander-devis')} className="btn-welly-outline">Demander un devis <span className="btn-welly-badge">↗</span></button>
            <button onClick={() => document.getElementById('particuliers')?.scrollIntoView({behavior:'smooth'})} className="btn-welly-outline">Télécharger l'app <span className="btn-welly-badge">↗</span></button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default App;
